import React, { useState, useEffect, useCallback, useMemo } from "react";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import IconButton from "../../../components/IconButton/IconButton";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import axiosInstance from "../../../axiosInstance";
import "./OperationStages.scss";
import { toast } from "react-toastify";
import { useAuth } from "../../../contexts/AuthContext";
import { Icon } from "@iconify/react";
import WarningPopup from "../../../components/WarningPopup/WarningPopup";

const OperationCategory = ({ markPageAsCompleted }) => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [warningPopup, setWarningPopup] = useState({
    isOpen: false,
    title: "",
    message: "",
    onAccept: null,
  });

  const [formData, setFormData] = useState({
    id: null,
    name: "",
    productIds: [],
    operationStages: [],
  });

  const SIDEBAR_COLLAPSED_KEY = "operation_stages_sidebar_collapsed";
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {}
      return next;
    });
  };

  const INFO_CARD_COLLAPSED_KEY = "operation_stages_info_card_collapsed";
  const [isInfoCardCollapsed, setIsInfoCardCollapsed] = useState(() => {
    try {
      return localStorage.getItem(INFO_CARD_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const toggleInfoCard = () => {
    setIsInfoCardCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(INFO_CARD_COLLAPSED_KEY, String(next));
      } catch {}
      return next;
    });
  };

  // Derived, real-data metric stats (no fabricated numbers)
  const flowStats = useMemo(() => {
    let totalStages = 0;
    let totalChecklistItems = 0;
    const productIds = new Set();

    categories.forEach((category) => {
      totalStages += category.operationStages?.length || 0;
      category.operationStages?.forEach((stage) => {
        totalChecklistItems += stage.checkLists?.length || 0;
      });
      category.products?.forEach((product) => {
        if (product?.id) productIds.add(product.id);
      });
    });

    return {
      totalFlows: categories.length,
      totalStages,
      totalChecklistItems,
      productsCovered: productIds.size,
    };
  }, [categories]);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_ERP_ADMIN_URL}/operation-categories`,
      );
      if (response.data?.status === 200 && response.data?.data) {
        setCategories(response.data.data);
        if (
          response.data.data.length > 0 &&
          typeof markPageAsCompleted === "function"
        ) {
          markPageAsCompleted(
            "Operation Stages (Product Manufacturing Stages)",
          );
        }
      } else setError("Failed to fetch operation categories");
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to fetch operation categories",
      );
    } finally {
      setLoading(false);
    }
  }, [markPageAsCompleted]);

  const handleDeleteCategory = async (categoryId) => {
    setWarningPopup({
      isOpen: true,
      title: "Delete Operation Category",
      message:
        "Are you sure you want to delete this operation category? This action cannot be undone and will remove all associated stages and checklists.",
      onAccept: async () => {
        try {
          const response = await axiosInstance.delete(
            `${process.env.REACT_APP_API_ERP_ADMIN_URL}/operation-categories/${categoryId}`,
          );

          if (response.data?.status === 200 || response.status === 200) {
            toast.success("Operation Category deleted successfully!");
            await fetchCategories();
          }
        } catch (error) {
          console.error("Error deleting category:", error);
        } finally {
          setWarningPopup({
            isOpen: false,
            title: "",
            message: "",
            onAccept: null,
          });
        }
      },
    });
  };

  const handleDeleteStage = async (stageId, stageIndex) => {
    setWarningPopup({
      isOpen: true,
      title: "Delete Operation Stage",
      message:
        "Are you sure you want to delete this stage? All associated checklist items will also be removed.",
      onAccept: async () => {
        try {
          const response = await axiosInstance.delete(
            `${process.env.REACT_APP_API_ERP_ADMIN_URL}/operation-categories/operationStage/${stageId}`,
          );

          if (response.data?.status === 200 || response.status === 200) {
            // remove locally and re-sequence
            const updatedStages = formData.operationStages
              .filter((_, i) => i !== stageIndex)
              .map((stage, idx) => ({ ...stage, sequenceNumber: idx + 1 }));

            setFormData((prev) => ({
              ...prev,
              operationStages: updatedStages,
            }));

            if (updatedStages.length > 0) {
              toast.success(
                "Operation stage deleted successfully! Remaining stage sequence numbers have been updated.",
              );
            } else {
              toast.success("Operation stage deleted successfully!");
            }

            await fetchCategories();
          }
        } catch (error) {
          console.error("Error deleting stage:", error);
          toast.error(
            error.response?.data?.message || "Failed to delete stage",
          );
        } finally {
          setWarningPopup({
            isOpen: false,
            title: "",
            message: "",
            onAccept: null,
          });
        }
      },
    });
  };

  // Add this helper function in your component
  const validateStageFlags = (stageIndex, field, value) => {
    if (!value) return true; // If unchecking, always allow

    const updatedStages = [...formData.operationStages];

    // Check if another stage already has this flag
    const existingIndex = updatedStages.findIndex(
      (stage, idx) => idx !== stageIndex && stage[field] === true,
    );

    if (existingIndex !== -1) {
      const flagName = field === "isInitialStage" ? "Initial" : "Final";
      const existingStageName =
        updatedStages[existingIndex].name || `Stage ${existingIndex + 1}`;
      toast.warning(
        `Only one ${flagName} stage is allowed. "${existingStageName}" is already marked as ${flagName} stage.`,
      );
      return false;
    }

    return true;
  };

  const handleDeleteChecklist = async (checklistId, stageIndex) => {
    setWarningPopup({
      isOpen: true,
      title: "Delete Checklist Item",
      message:
        "Are you sure you want to delete this checklist item? This action cannot be undone.",
      onAccept: async () => {
        try {
          const response = await axiosInstance.delete(
            `${process.env.REACT_APP_API_ERP_ADMIN_URL}/operation-categories/checklist/${checklistId}`,
          );

          if (response.data?.status === 200 || response.status === 200) {
            toast.success("Checklist item deleted successfully!");

            // Update the stage in formData to remove the deleted checklist
            const updatedStages = [...formData.operationStages];
            updatedStages[stageIndex].checkLists = updatedStages[
              stageIndex
            ].checkLists.filter((checklist) => checklist.id !== checklistId);
            setFormData({ ...formData, operationStages: updatedStages });

            await fetchCategories();
          }
        } catch (error) {
          console.error("Error deleting checklist:", error);
        } finally {
          setWarningPopup({
            isOpen: false,
            title: "",
            message: "",
            onAccept: null,
          });
        }
      },
    });
  };

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_ERP_ADMIN_URL}/products`,
      );
      if (response.data?.status === 200 && response.data?.data)
        setProducts(response.data.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/employees/operations/list`,
      );
      if (response.data?.status === 200 && response.data?.data)
        setEmployees(response.data.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchCategories();
      fetchProducts();
      fetchEmployees();
    }
  }, [user, fetchCategories, fetchProducts, fetchEmployees]);

  const handleOpenForm = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        id: category.id,
        name: category.name,
        productIds: category.productIds || [],
        operationStages: (category.operationStages || [])
          .slice()
          .sort((a, b) => a.sequenceNumber - b.sequenceNumber || a.id - b.id)
          .map((s, idx) => {
            // Don't carry `assignedEmployees` into formData — it's a
            // read-only snapshot from the GET response and never gets
            // updated when checkboxes are toggled (only `employeeIds` is).
            // Keeping it around risks it being sent back stale on submit.
            const { assignedEmployees, ...stageRest } = s;
            return {
              ...stageRest,
              sequenceNumber: idx + 1,
              status: s.status?.toUpperCase?.() || "ENABLED",
              employeeIds:
                assignedEmployees?.map((emp) => emp.empId ?? emp.id) ||
                s.employeeIds ||
                [],
            };
          }),
      });
    } else {
      setEditingCategory(null);
      setFormData({
        id: null,
        name: "",
        productIds: [],
        operationStages: [
          {
            id: null,
            name: "",
            description: "",
            sequenceNumber: 1,
            isInitialStage: true,
            isFinalStage: false,
            status: "ENABLED",
            checkLists: [],
            employeeIds: [],
          },
        ],
      });
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingCategory(null);
    setFormData({
      id: null,
      name: "",
      productIds: [],
      operationStages: [],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Category name is required");
      return;
    }

    try {
      // Validate for clashing sequence numbers before submitting
      const seenSequences = new Map();
      for (let i = 0; i < formData.operationStages.length; i++) {
        const seq = formData.operationStages[i].sequenceNumber;
        if (seenSequences.has(seq)) {
          const clashIndex = seenSequences.get(seq);
          const stageName =
            formData.operationStages[i].name || `Stage ${i + 1}`;
          const clashName =
            formData.operationStages[clashIndex].name ||
            `Stage ${clashIndex + 1}`;
          toast.error(
            `Sequence number ${seq} is used by both "${clashName}" and "${stageName}". Please make each stage sequence unique.`,
          );
          const stageEl = document.getElementById(`stage-card-${i}`);
          if (stageEl)
            stageEl.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
        seenSequences.set(seq, i);
      }

      const normalizedStages = formData.operationStages.map((stage) => {
        // Drop the stale `assignedEmployees` snapshot from the original GET
        // response — it's never updated when checkboxes are toggled, only
        // `employeeIds` is. Sending both lets the backend see the old list.
        const { assignedEmployees, ...stageWithoutAssignedEmployees } = stage;
        return {
          ...stageWithoutAssignedEmployees,
          status: stage.status?.toUpperCase?.() || "ENABLED",
        };
      });

      const payload = {
        ...formData,
        operationStages: normalizedStages,
      };

      console.log(payload);

      const method = editingCategory ? "put" : "post";
      const response = await axiosInstance[method](
        `${process.env.REACT_APP_API_ERP_ADMIN_URL}/operation-categories`,
        payload,
      );

      if (response.data?.status === 200) {
        toast.success(
          `Operation Flow ${
            editingCategory ? "updated" : "created"
          } successfully!`,
        );

        await fetchCategories();
        handleCloseForm();
      }
    } catch (error) {
      console.error("Error saving category:", error);
    }
  };

  const addStage = () => {
    const updatedStages = [
      ...formData.operationStages,
      {
        id: null,
        name: "",
        description: "",
        sequenceNumber: formData.operationStages.length + 1,
        isInitialStage: formData.operationStages.length === 0,
        isFinalStage: false,
        status: "ENABLED",
        checkLists: [],
        employeeIds: [],
      },
    ];

    // Re-sequence all stages to ensure no clashes
    const resequenced = updatedStages.map((stage, idx) => ({
      ...stage,
      sequenceNumber: idx + 1,
    }));

    setFormData({ ...formData, operationStages: resequenced });
  };

  const removeStage = async (index) => {
    const stage = formData.operationStages[index];

    // if stage exists in DB → delete via API
    if (stage.id) {
      await handleDeleteStage(stage.id, index);
    } else {
      // new unsaved stage → remove locally and re-sequence
      const updatedStages = formData.operationStages
        .filter((_, i) => i !== index)
        .map((stage, idx) => ({ ...stage, sequenceNumber: idx + 1 }));

      setFormData((prev) => ({
        ...prev,
        operationStages: updatedStages,
      }));
    }
  };

  const updateStage = (index, field, value) => {
    const updatedStages = [...formData.operationStages];
    updatedStages[index] = { ...updatedStages[index], [field]: value };
    setFormData({ ...formData, operationStages: updatedStages });
  };

  const addChecklist = (stageIndex) => {
    const updatedStages = [...formData.operationStages];
    updatedStages[stageIndex].checkLists.push({
      id: null,
      itemName: "",
      description: "",
      isMandatory: false,
      sequenceNumber: updatedStages[stageIndex].checkLists.length + 1,
    });
    setFormData({ ...formData, operationStages: updatedStages });
  };

  const handleChecklistKeyDown = (e, stageIndex, checklistIndex) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation(); // Prevent form submission
      const updatedStages = [...formData.operationStages];
      const currentChecklist =
        updatedStages[stageIndex].checkLists[checklistIndex];

      // Only add new checklist if current one has a name
      if (currentChecklist.itemName.trim()) {
        addChecklist(stageIndex);

        // Focus the new input after a short delay to allow DOM update
        setTimeout(() => {
          const newIndex = checklistIndex + 1;
          const newInput = document.querySelector(
            `[data-checklist-input="${stageIndex}-${newIndex}"]`,
          );
          if (newInput) {
            newInput.focus();
          }
        }, 50);
      }
    }
  };

  const removeChecklist = async (stageIndex, checklistIndex) => {
    const checklist =
      formData.operationStages[stageIndex].checkLists[checklistIndex];

    // If checklist has an ID, it exists in the database - delete via API
    if (checklist.id) {
      await handleDeleteChecklist(checklist.id, stageIndex);
    } else {
      // If no ID, it's a new checklist not yet saved - just remove locally
      const updatedStages = [...formData.operationStages];
      updatedStages[stageIndex].checkLists = updatedStages[
        stageIndex
      ].checkLists.filter((_, i) => i !== checklistIndex);
      setFormData({ ...formData, operationStages: updatedStages });
    }
  };

  const updateChecklist = (stageIndex, checklistIndex, field, value) => {
    const updatedStages = [...formData.operationStages];
    updatedStages[stageIndex].checkLists[checklistIndex] = {
      ...updatedStages[stageIndex].checkLists[checklistIndex],
      [field]: value,
    };
    setFormData({ ...formData, operationStages: updatedStages });
  };

  const toggleStageEmployee = (stageIndex, employeeId) => {
    setFormData((prev) => {
      const updatedStages = prev.operationStages.map((stage, idx) => {
        if (idx !== stageIndex) return stage;
        const currentEmployees = stage.employeeIds || [];
        const employeeIds = currentEmployees.includes(employeeId)
          ? currentEmployees.filter((id) => id !== employeeId)
          : [...currentEmployees, employeeId];
        return { ...stage, employeeIds };
      });
      return { ...prev, operationStages: updatedStages };
    });
  };

  const toggleProduct = (productId) => {
    if (formData.productIds.includes(productId)) {
      setFormData({
        ...formData,
        productIds: formData.productIds.filter((id) => id !== productId),
      });
    } else {
      setFormData({
        ...formData,
        productIds: [...formData.productIds, productId],
      });
    }
  };

  if (loading && categories.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div className="content-area operation-category-container">
      <div className="operation-category-header">
        <h2 className="display6">
          Operation Flows (Product Manufacturing Stages)
        </h2>
      </div>

      {/* Metric summary tiles - derived from real flow/stage/checklist data */}
      <div className="operation-category-container__metrics-grid">
        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Operation Flows</span>
            <span className="metric-tile__value">{flowStats.totalFlows}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--neutral">
            <Icon icon="mdi:folder-multiple-outline" />
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Total Stages</span>
            <span className="metric-tile__value">{flowStats.totalStages}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--success">
            <Icon icon="mdi:timeline-check-outline" />
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Checklist Items</span>
            <span className="metric-tile__value">
              {flowStats.totalChecklistItems}
            </span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--info">
            <Icon icon="mdi:checkbox-marked-outline" />
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Products Covered</span>
            <span className="metric-tile__value">
              {flowStats.productsCovered}
            </span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--warning">
            <Icon icon="mdi:package-variant" />
          </div>
        </div>
      </div>

      {/* Helpful tip */}
      <div className="operation-category-container__tip-banner">
        <span
          className="operation-category-container__tip-icon"
          aria-hidden="true"
        >
          <Icon icon="mdi:lightbulb-on-outline" />
        </span>
        <p>
          <strong>Tip:</strong> Mark exactly one stage as the Start and one as
          the End of each flow. Every stage in between runs in the sequence
          order you set.
        </p>
      </div>

      <div className="operation-category-container__actions">
        <button
          type="button"
          className={`action-card ${showForm ? "action-card--active" : ""}`}
          onClick={() => {
            if (showForm) {
              handleCloseForm();
            } else {
              handleOpenForm();
            }
          }}
        >
          <span className="action-card-icon" aria-hidden="true">
            <Icon icon={showForm ? "mdi:close" : "mdi:plus"} />
          </span>
          <span className="action-card-copy">
            <span className="action-card-title">
              {showForm ? "Close Form" : "Create Flow"}
            </span>
            <span className="action-card-desc">
              {showForm
                ? "Hide the flow form"
                : "Set up a new manufacturing flow"}
            </span>
          </span>
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">
            ×
          </button>
        </div>
      )}
      {/* Inline Form */}
      {showForm && (
        <div className="inline-form-container">
          <div className="form-header">
            <div>
              <h3 className="form-title">
                {editingCategory ? "Edit" : "Create"} Operation Category
              </h3>
              <p className="form-subtitle">
                {editingCategory
                  ? "Update flow details and manage operation stages"
                  : "Set up a new flow with products and workflow stages"}
              </p>
            </div>
            <div className="progress-indicator">
              <div className="step active">
                <span className="step-number">1</span>
                <span className="step-label">Basic Info</span>
              </div>
              <div className="step-divider"></div>
              <div className="step active">
                <span className="step-number">2</span>
                <span className="step-label">Products</span>
              </div>
              <div className="step-divider"></div>
              <div className="step active">
                <span className="step-number">3</span>
                <span className="step-label">Stages</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Step 1: Basic Info */}
            <div className="form-section">
              <div className="section-header">
                <Icon icon="mdi:information-outline" className="section-icon" />
                <div>
                  <h4 className="section-title">Basic Information</h4>
                  <p className="section-description">
                    Give your category a clear, descriptive name
                  </p>
                </div>
              </div>
              <div className="form-group">
                <label className="label required">Operation Flow Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., Electronics Assembly Flow, Furniture Manufacturing Process"
                />
              </div>
            </div>

            {/* Step 2: Products */}
            <div className="form-section">
              <div className="section-header">
                <Icon icon="mdi:package-variant" className="section-icon" />
                <div>
                  <h4 className="section-title">Assign Products</h4>
                  <p className="section-description">
                    Select products that will follow this operation workflow
                    {formData.productIds.length > 0 && (
                      <span className="count-badge">
                        {formData.productIds.length} selected
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="checkbox-grid-compact">
                {products.length > 0 ? (
                  products.map((product) => (
                    <label key={product.id} className="checkbox-label-compact">
                      <input
                        type="checkbox"
                        checked={formData.productIds.includes(product.id)}
                        onChange={() => toggleProduct(product.id)}
                      />
                      <span>{product.name}</span>
                    </label>
                  ))
                ) : (
                  <div className="empty-message">
                    <Icon icon="mdi:alert-circle-outline" />
                    <span>No products available. Add products first.</span>
                  </div>
                )}
              </div>
            </div>

            {/* Operation Stages */}
            <div className="form-section">
              <div className="section-header">
                <Icon icon="mdi:timeline-outline" className="section-icon" />
                <div className="section-header-content">
                  <h4 className="section-title">Operation Stages</h4>
                  <p className="section-description">
                    Define the workflow steps for this category. Each stage can
                    have employees and checklists.
                    {formData.operationStages.length > 0 && (
                      <span className="count-badge">
                        {formData.operationStages.length} stage
                        {formData.operationStages.length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {formData.operationStages.length === 0 ? (
                <div className="empty-stages">
                  <Icon icon="mdi:workflow" />
                  <p>No stages added yet</p>
                  <span>
                    Click "Add Stage" to create your first workflow step
                  </span>
                </div>
              ) : (
                <div className="stages-list-compact">
                  {formData.operationStages.map((stage, stageIndex) => (
                    <div
                      key={stageIndex}
                      id={`stage-card-${stageIndex}`}
                      className="stage-card-compact"
                    >
                      <div className="stage-card-header">
                        <div className="stage-header-left">
                          <div className="stage-tags">
                            {stage.isInitialStage && (
                              <span className="tag tag-initial">
                                <Icon icon="mdi:play-circle" />
                                Start
                              </span>
                            )}
                            {stage.isFinalStage && (
                              <span className="tag tag-final">
                                <Icon icon="mdi:check-circle" />
                                End
                              </span>
                            )}
                          </div>
                        </div>
                        <IconButton
                          type="button"
                          icon="mdi:delete"
                          onClick={() => removeStage(stageIndex)}
                          variant="delete"
                          size="sm"
                          title="Delete Stage"
                        />
                      </div>

                      <div className="stage-form-grid">
                        <div className="form-group">
                          <label className="label-sm required">
                            <Icon icon="mdi:tag" />
                            Stage Name
                          </label>
                          <input
                            type="text"
                            className="form-input-sm"
                            value={stage.name}
                            onChange={(e) =>
                              updateStage(stageIndex, "name", e.target.value)
                            }
                            placeholder="e.g., Assembly, Quality Check"
                          />
                        </div>

                        <div className="form-group">
                          <label className="label-sm">
                            <Icon icon="mdi:order-numeric-ascending" />
                            Sequence
                          </label>
                          <input
                            type="number"
                            className="form-input-sm"
                            value={stage.sequenceNumber}
                            onChange={(e) =>
                              updateStage(
                                stageIndex,
                                "sequenceNumber",
                                parseInt(e.target.value),
                              )
                            }
                            min="1"
                          />
                        </div>

                        <div className="form-group description-group">
                          <label className="label-sm">
                            <Icon icon="mdi:text" />
                            Description (Optional)
                          </label>
                          <input
                            type="text"
                            className="form-input-sm"
                            value={stage.description}
                            onChange={(e) =>
                              updateStage(
                                stageIndex,
                                "description",
                                e.target.value,
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                e.stopPropagation();
                              }
                            }}
                            placeholder="Brief description of what happens in this stage"
                          />
                        </div>

                        <div className="form-group full-width">
                          <label className="label-sm">
                            <Icon icon="mdi:flag" />
                            Stage Type
                          </label>
                          <div className="checkbox-row">
                            <label className="checkbox-label-inline">
                              <input
                                type="checkbox"
                                checked={stage.isInitialStage}
                                onChange={(e) => {
                                  if (
                                    validateStageFlags(
                                      stageIndex,
                                      "isInitialStage",
                                      e.target.checked,
                                    )
                                  ) {
                                    updateStage(
                                      stageIndex,
                                      "isInitialStage",
                                      e.target.checked,
                                    );
                                  }
                                }}
                              />
                              <span>
                                Initial Stage (First step in workflow)
                              </span>
                            </label>
                            <label className="checkbox-label-inline">
                              <input
                                type="checkbox"
                                checked={stage.isFinalStage}
                                onChange={(e) => {
                                  if (
                                    validateStageFlags(
                                      stageIndex,
                                      "isFinalStage",
                                      e.target.checked,
                                    )
                                  ) {
                                    updateStage(
                                      stageIndex,
                                      "isFinalStage",
                                      e.target.checked,
                                    );
                                  }
                                }}
                              />
                              <span>Final Stage (Last step in workflow)</span>
                            </label>
                          </div>
                        </div>

                        {/* Employees */}
                        <div className="form-group full-width">
                          <label className="label-sm">
                            <Icon icon="mdi:account-group" />
                            Assign Employees
                            {(stage.employeeIds || []).length > 0 && (
                              <span className="count-badge-sm">
                                {(stage.employeeIds || []).length} assigned
                              </span>
                            )}
                          </label>
                          <div className="info-helper">
                            <Icon icon="mdi:information" />
                            Select employees who can work on this stage
                          </div>
                          {employees.length > 0 ? (
                            <div className="checkbox-grid-compact">
                              {employees.map((employee) => (
                                <label
                                  key={employee.empId}
                                  className="checkbox-label-compact"
                                >
                                  <input
                                    type="checkbox"
                                    checked={(stage.employeeIds || []).includes(
                                      employee.empId,
                                    )}
                                    onChange={() =>
                                      toggleStageEmployee(
                                        stageIndex,
                                        employee.empId,
                                      )
                                    }
                                  />
                                  <span>
                                    {employee.firstName} {employee.lastName}
                                  </span>
                                </label>
                              ))}
                            </div>
                          ) : (
                            <div className="empty-message">
                              <Icon icon="mdi:alert-circle-outline" />
                              <span>No employees available</span>
                            </div>
                          )}
                        </div>

                        {/* Checklists */}
                        <div className="form-group full-width">
                          <div className="subsection-header-inline">
                            <label className="label-sm">
                              <Icon icon="mdi:checkbox-marked-outline" />
                              Checklists
                              {stage.checkLists.length > 0 && (
                                <span className="count-badge-sm">
                                  {stage.checkLists.length} item
                                  {stage.checkLists.length !== 1 ? "s" : ""}
                                </span>
                              )}
                            </label>
                            <button
                              type="button"
                              className="add-btn-text"
                              onClick={() => addChecklist(stageIndex)}
                            >
                              <Icon icon="mdi:plus" width="14" height="14" />
                              Add Item
                            </button>
                          </div>
                          <div className="info-helper">
                            <Icon icon="mdi:information" />
                            Create checklist items that need to be verified in
                            this stage
                          </div>

                          {stage.checkLists.length === 0 ? (
                            <div className="empty-checklist">
                              <Icon icon="mdi:clipboard-text-outline" />
                              <span>
                                No checklist items. Click "Add Item" to create
                                one.
                              </span>
                            </div>
                          ) : (
                            stage.checkLists.map(
                              (checklist, checklistIndex) => (
                                <div
                                  key={checklistIndex}
                                  className="checklist-row-compact"
                                >
                                  <span className="checklist-index">
                                    {checklistIndex + 1}
                                  </span>
                                  <input
                                    type="text"
                                    className="form-input-sm"
                                    value={checklist.itemName}
                                    onChange={(e) =>
                                      updateChecklist(
                                        stageIndex,
                                        checklistIndex,
                                        "itemName",
                                        e.target.value,
                                      )
                                    }
                                    onKeyDown={(e) =>
                                      handleChecklistKeyDown(
                                        e,
                                        stageIndex,
                                        checklistIndex,
                                      )
                                    }
                                    data-checklist-input={`${stageIndex}-${checklistIndex}`}
                                    placeholder="Checklist item name"
                                  />
                                  <input
                                    type="number"
                                    className="form-input-sm seq-input"
                                    value={checklist.sequenceNumber}
                                    onChange={(e) =>
                                      updateChecklist(
                                        stageIndex,
                                        checklistIndex,
                                        "sequenceNumber",
                                        parseInt(e.target.value),
                                      )
                                    }
                                    placeholder="Order"
                                    min="1"
                                    title="Display order"
                                  />
                                  <label
                                    className="checkbox-label-inline"
                                    title="Mark as mandatory to complete"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checklist.isMandatory}
                                      onChange={(e) =>
                                        updateChecklist(
                                          stageIndex,
                                          checklistIndex,
                                          "isMandatory",
                                          e.target.checked,
                                        )
                                      }
                                    />
                                    <span>Required</span>
                                  </label>
                                  <button
                                    type="button"
                                    className="delete-btn-sm"
                                    onClick={() =>
                                      removeChecklist(
                                        stageIndex,
                                        checklistIndex,
                                      )
                                    }
                                    title="Remove checklist item"
                                  >
                                    <Icon
                                      icon="mdi:close"
                                      width="16"
                                      height="16"
                                    />
                                  </button>
                                </div>
                              ),
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button
                type="button"
                className="add-btn-primary"
                onClick={addStage}
              >
                <Icon icon="mdi:plus" width="18" height="18" />
                Add Stage
              </button>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <ButtonWithIcon
                icon="mdi:cancel"
                text="Cancel"
                className="cancel-button "
                onClick={handleCloseForm}
                variant="secondary"
              />
              <ButtonWithIcon
                icon="mdi:check"
                text={editingCategory ? "Update Stage" : "Create Stage"}
                className={editingCategory ? "edit-button" : "submit-button"}
                variant="primary"
                type="submit"
              />
            </div>
          </form>
        </div>
      )}
      {/* Categories Section - NEW LAYOUT */}

      <div
        className={`operation-category-container__layout ${
          isSidebarCollapsed
            ? "operation-category-container__layout--collapsed"
            : ""
        }`}
      >
        <div className="operation-category-container__main">
          <div className="categories-section">
            {categories.length === 0 && !loading ? (
              <div className="empty-state">
                <Icon icon="mdi:folder-open-outline" />
                <h4>No Categories Yet</h4>
                <p>
                  Operation flows define how a product moves through
                  manufacturing. To get started:
                </p>
                <ol className="operation-category-container__empty-steps">
                  <li>Create your first operation flow</li>
                  <li>Assign the products it covers</li>
                  <li>Add stages and mark the Start and End</li>
                </ol>
                <button
                  type="button"
                  className="action-card action-card--primary"
                  onClick={() => handleOpenForm()}
                >
                  <span className="action-card-copy">
                    <span className="action-card-title">Create Flow</span>
                    <span className="action-card-desc">
                      Set up your first operation flow
                    </span>
                  </span>
                </button>
              </div>
            ) : (
              categories.map((category) => (
                <div key={category.id} className="flow-card">
                  <div className="flow-card__header">
                    <div className="flow-card__identity">
                      <span className="flow-card__icon" aria-hidden="true">
                        <Icon icon="mdi:folder-outline" />
                      </span>
                      <div className="flow-card__identity-copy">
                        <h3 className="flow-card__name">{category.name}</h3>
                        {category.products && category.products.length > 0 && (
                          <div className="flow-card__products">
                            {category.products.slice(0, 3).map((product) => (
                              <span key={product.id} className="flow-chip">
                                {product.name}
                              </span>
                            ))}
                            {category.products.length > 3 && (
                              <span className="flow-chip-more-wrapper">
                                <span
                                  className="flow-chip flow-chip--more"
                                  tabIndex={0}
                                >
                                  +{category.products.length - 3} more
                                </span>
                                <div className="flow-chip-tooltip">
                                  <div className="flow-chip-tooltip__title">
                                    All products in this flow
                                  </div>
                                  <div className="flow-chip-tooltip__list">
                                    {category.products.map((product) => (
                                      <span
                                        key={product.id}
                                        className="flow-chip-tooltip__item"
                                      >
                                        {product.name}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flow-card__stats">
                      <div className="flow-stat">
                        <span className="flow-stat__value">
                          {category.operationStages?.length || 0}
                        </span>
                        <span className="flow-stat__label">Stages</span>
                      </div>
                      <div className="flow-stat">
                        <span className="flow-stat__value">
                          {category.products?.length || 0}
                        </span>
                        <span className="flow-stat__label">Products</span>
                      </div>
                    </div>

                    <div className="flow-card__actions">
                      <IconButton
                        icon="mdi:pencil"
                        onClick={() => handleOpenForm(category)}
                        variant="edit"
                        size="sm"
                        type="button"
                        title="Edit Category"
                      />
                      <IconButton
                        icon="mdi:delete"
                        onClick={() => handleDeleteCategory(category.id)}
                        variant="delete"
                        size="sm"
                        type="button"
                        title="Delete Category"
                      />
                    </div>
                  </div>

                  <div className="flow-card__body">
                    {category.operationStages &&
                    category.operationStages.length > 0 ? (
                      <div className="flow-pipeline">
                        {category.operationStages.map((stage, index) => {
                          const isLast =
                            index === category.operationStages.length - 1;
                          const sortedChecklist = [
                            ...(stage.checkLists || []),
                          ].sort((a, b) => a.sequenceNumber - b.sequenceNumber);
                          return (
                            <div
                              key={stage.id || index}
                              className="pipeline-stage"
                            >
                              <div className="pipeline-stage__rail">
                                <span
                                  className={`pipeline-stage__dot ${
                                    stage.isInitialStage
                                      ? "pipeline-stage__dot--start"
                                      : ""
                                  } ${
                                    stage.isFinalStage
                                      ? "pipeline-stage__dot--end"
                                      : ""
                                  }`}
                                >
                                  {index + 1}
                                </span>
                                {!isLast && (
                                  <span className="pipeline-stage__line" />
                                )}
                              </div>

                              <div className="pipeline-stage__content">
                                <div className="pipeline-stage__top">
                                  <h4 className="pipeline-stage__name">
                                    {stage.name}
                                  </h4>
                                  <div className="pipeline-stage__badges">
                                    {stage.isInitialStage && (
                                      <span className="flow-badge flow-badge--start">
                                        <Icon icon="mdi:flag-outline" />
                                        Start
                                      </span>
                                    )}
                                    {stage.isFinalStage && (
                                      <span className="flow-badge flow-badge--end">
                                        <Icon icon="mdi:flag-checkered" />
                                        End
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {stage.description && (
                                  <p className="pipeline-stage__desc">
                                    {stage.description}
                                  </p>
                                )}

                                <div className="pipeline-stage__meta">
                                  <span className="meta-chip">
                                    <Icon icon="mdi:account-group-outline" />
                                    {stage.assignedEmployees &&
                                    stage.assignedEmployees.length > 0
                                      ? stage.assignedEmployees
                                          .map(
                                            (e) =>
                                              `${e.firstName} ${e.lastName}`,
                                          )
                                          .join(", ")
                                      : "No employees assigned"}
                                  </span>
                                  <span className="meta-chip">
                                    <Icon icon="mdi:checkbox-marked-circle-outline" />
                                    {sortedChecklist.length > 0
                                      ? `${sortedChecklist.length} checklist item${
                                          sortedChecklist.length !== 1
                                            ? "s"
                                            : ""
                                        }`
                                      : "No checklist items"}
                                  </span>
                                </div>

                                {sortedChecklist.length > 0 && (
                                  <div className="pipeline-stage__checklist">
                                    {sortedChecklist.map((checklist, idx) => (
                                      <div
                                        key={checklist.id || idx}
                                        className="checklist-row"
                                      >
                                        <Icon
                                          icon="mdi:checkbox-blank-circle-outline"
                                          className="checklist-row__icon"
                                        />
                                        <div className="checklist-row__content">
                                          <div className="checklist-row__top">
                                            <span className="checklist-row__name">
                                              {checklist.itemName}
                                            </span>
                                            {checklist.isMandatory && (
                                              <span className="checklist-row__required">
                                                Required
                                              </span>
                                            )}
                                          </div>
                                          {checklist.description && (
                                            <p className="checklist-row__desc">
                                              {checklist.description}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flow-card__no-stages">
                        <Icon icon="mdi:alert-circle-outline" />
                        <span>No operation stages defined</span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <button
          type="button"
          className="operation-category-container__sidebar-toggle"
          onClick={toggleSidebar}
          aria-expanded={!isSidebarCollapsed}
          aria-label={isSidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
          title={isSidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d={isSidebarCollapsed ? "m9 6 6 6-6 6" : "m15 6-6 6 6 6"}
            />
          </svg>
        </button>

        <aside
          className="operation-category-container__sidebar"
          aria-hidden={isSidebarCollapsed}
        >
          <div className="sidebar-card sidebar-card--info">
            <button
              type="button"
              className={`sidebar-card__header ${
                !isInfoCardCollapsed ? "is-expanded" : ""
              }`}
              onClick={toggleInfoCard}
              aria-expanded={!isInfoCardCollapsed}
            >
              <h4>How It Works</h4>
              <svg
                className={`sidebar-card__chevron ${
                  isInfoCardCollapsed ? "is-collapsed" : ""
                }`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m6 9 6 6 6-6"
                />
              </svg>
            </button>
            <div
              className={`sidebar-card__body ${
                isInfoCardCollapsed ? "is-collapsed" : ""
              }`}
            >
              <div className="sidebar-card__body-inner">
                <ol className="sidebar-steps">
                  <li>
                    <strong>Create Flow</strong>
                    <span>
                      Name the flow and pick the products it applies to.
                    </span>
                  </li>
                  <li>
                    <strong>Add Stages</strong>
                    <span>
                      Define each manufacturing step in sequence order.
                    </span>
                  </li>
                  <li>
                    <strong>Mark Start &amp; End</strong>
                    <span>
                      Flag exactly one stage as the Start and one as the End.
                    </span>
                  </li>
                  <li>
                    <strong>Assign &amp; Track</strong>
                    <span>
                      Add employees and checklists so each stage is accountable.
                    </span>
                  </li>
                </ol>
              </div>
            </div>
          </div>

          <div className="sidebar-card sidebar-card--success">
            <h4>Best Practices</h4>
            <ul className="sidebar-list">
              <li>Keep stage names short and action-oriented</li>
              <li>Use checklists to standardize quality checks per stage</li>
              <li>Assign employees so accountability is clear at each step</li>
              <li>Review flows when your product lineup changes</li>
            </ul>
          </div>

          <div className="sidebar-card sidebar-card--warning">
            <h4>Important Note</h4>
            <p>
              Deleting a flow, stage or checklist item is permanent and cannot
              be undone. Products relying on a deleted flow will need a new one
              assigned.
            </p>
          </div>
        </aside>
      </div>

      <WarningPopup
        isOpen={warningPopup.isOpen}
        onClose={() =>
          setWarningPopup({
            isOpen: false,
            title: "",
            message: "",
            onAccept: null,
          })
        }
        onAccept={warningPopup.onAccept}
        onDecline={() =>
          setWarningPopup({
            isOpen: false,
            title: "",
            message: "",
            onAccept: null,
          })
        }
        title={warningPopup.title}
        message={warningPopup.message}
        acceptText="Delete"
        declineText="Cancel"
      />
    </div>
  );
};

export default OperationCategory;
